import {
    ActionButton,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    SearchInput,
} from "@jhool-io/fe-components";
import React from "react";
import { useSearchParams } from "react-router-dom";
import moment from "../../../../../utils/moment";
import TableFiltersDisplay from "../../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { useFetchClientList } from "../../../../../hooks/queries/client";
import { UserRole } from "../../../../user-management/types/user-management.types";
import {
    displayNameInRightFormat,
    isSmallScreen,
} from "../../../../../utils/helpers";
import { useFetchProviders } from "../../../../providers/hooks/providers.queries";
import ScheduleWeekView from "../ScheduleWeekView/ScheduleWeekView";
import { useDebounce } from "../../../../../hooks/helpers";
import ListState from "../../../../../components/ListState/ListState";
import { useAppSelector } from "../../../../../hooks/useRedux";
import Loader from "../../../../../shared-ui/Loader/Loader";
import ChevronLeftIcon from "../../../../../icons/ChevronLeft";
import ChevronRightIcon from "../../../../../icons/ChevronRight";

export default function BillerScheduleWrapper() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentWeek, setCurrentWeek] = React.useState(
        moment().startOf("week")
    );
    const { practice } = useAppSelector((state) => state.userPractice);

    const [clientSearchString, setClientSearchString] = React.useState("");
    const [providerSearchString, setProviderSearchString] = React.useState("");
    const clientFilter = searchParams.getAll("client_ids[]") || [];
    const providerFilter = searchParams.getAll("provider_ids[]") || [];

    const handleSetProviderId = () => {
        if (
            !practice?.roles?.includes(UserRole.AUDITOR) &&
            practice?.roles?.includes(UserRole.PROVIDER)
        ) {
            return practice?.provider_id;
        }

        if (providerFilter.length === 1) {
            return providerFilter[0];
        }

        return "";
    };

    const debouncedProviderSearchValue = useDebounce(providerSearchString, 500);
    const debouncedClientSearchValue = useDebounce(clientSearchString, 500);

    const { data, isLoading, error } = useFetchClientList({
        page: 1,
        search_string: debouncedClientSearchValue,
        provider_id: handleSetProviderId() as string,
    });

    const {
        data: providers,
        isLoading: providerListLoading,
        error: providerListError,
    } = useFetchProviders({
        page: 1,
        search_string: debouncedProviderSearchValue,
        limit: 20,
    });

    // Function to handle week navigation
    const updateWeekRange = (direction: string) => {
        const newWeek =
            direction === "next"
                ? moment(currentWeek).add(1, "week")
                : moment(currentWeek).subtract(1, "week");
        searchParams.set(
            "from_date",
            newWeek.startOf("week").format("YYYY-MM-DD")
        );
        searchParams.set("to_date", newWeek.endOf("week").format("YYYY-MM-DD"));
        setSearchParams(searchParams);
        setCurrentWeek(newWeek);
    };

    const handleClientFilter = (client_id: string) => {
        if (clientFilter.includes(client_id)) {
            searchParams.delete("client_ids[]", client_id);
        } else {
            searchParams.append("client_ids[]", client_id);
        }
        setSearchParams(searchParams);
    };

    const handleProviderFilter = (provider_id: string) => {
        if (providerFilter.includes(provider_id)) {
            searchParams.delete("provider_ids[]", provider_id);
        } else {
            searchParams.append("provider_ids[]", provider_id);
        }
        setSearchParams(searchParams);
    };

    const startOfWeek = currentWeek.startOf("week").format("MMM D");
    const endOfWeek = currentWeek.endOf("week").format("MMM D, YYYY");

    const currentWeekRange = `${startOfWeek} - ${endOfWeek}`;

    const filteredClientNames = data?.data
        .filter((client) => clientFilter.includes(client.client_id))
        .map((client) =>
            displayNameInRightFormat({
                firstName: client.first_name,
                lastName: client.last_name,
            })
        );

    const filteredProviderNames = providers?.data
        .filter((provider) => providerFilter.includes(provider.provider_id))
        .map((provider) => displayNameInRightFormat(provider.provider_name));

    return (
        <div className="relative">
            <Card classNames="h-fit sticky top-6 z-[4]">
                <div className="flex items-center">
                    <div className="flex gap-x-[--spacing-items]">
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>Providers</FilterButton>
                            </DropdownTrigger>
                            <DropdownContent
                                align="end"
                                width="300px"
                                collisionPadding={10}
                            >
                                <SearchInput
                                    placeholder="Search by provider name"
                                    containerClass="shrink-0 mb-1"
                                    onKeyDown={(e) => {
                                        if (e.key !== "Escape") {
                                            e.stopPropagation();
                                        }
                                    }}
                                    onChange={(e) =>
                                        setProviderSearchString(e.target.value)
                                    }
                                />
                                {providerListLoading && <Loader />}
                                {providerListError && (
                                    <ListState
                                        isError
                                        context="general"
                                        stateHelperText="Please try again later"
                                        errorMsg={
                                            providerListError?.response?.data
                                                ?.message ||
                                            "Error loading clinicians"
                                        }
                                        cardClass="h-[150px]"
                                    />
                                )}
                                {providers?.data?.map((provider) => (
                                    <DropdownItem
                                        key={provider.provider_id}
                                        onClick={() =>
                                            handleProviderFilter(
                                                provider.provider_id
                                            )
                                        }
                                        isMulti
                                        itemValue={provider.provider_id}
                                        values={providerFilter}
                                        closeMenuOnSelect
                                    >
                                        {displayNameInRightFormat(
                                            provider.provider_name
                                        )}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>

                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>Clients</FilterButton>
                            </DropdownTrigger>
                            <DropdownContent
                                align={isSmallScreen() ? "start" : "end"}
                                width="300px"
                                collisionPadding={10}
                            >
                                <SearchInput
                                    placeholder="Search by client name"
                                    containerClass="shrink-0 mb-1"
                                    onKeyDown={(e) => {
                                        if (e.key !== "Escape") {
                                            e.stopPropagation();
                                        }
                                    }}
                                    onChange={(e) => {
                                        setClientSearchString(e.target.value);
                                    }}
                                />
                                {isLoading && <Loader />}
                                {error && (
                                    <ListState
                                        isError
                                        context="general"
                                        stateHelperText="Please try again later"
                                        errorMsg={
                                            providerListError?.response?.data
                                                ?.message ||
                                            "Error loading clients"
                                        }
                                        cardClass="h-[150px]"
                                    />
                                )}
                                {data?.data?.map((client) => (
                                    <DropdownItem
                                        key={client.client_id}
                                        onClick={() =>
                                            handleClientFilter(client.client_id)
                                        }
                                        isMulti
                                        itemValue={client.client_id}
                                        values={clientFilter}
                                        closeMenuOnSelect
                                    >
                                        {displayNameInRightFormat({
                                            firstName: client.first_name,
                                            lastName: client.last_name,
                                        })}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                    </div>
                    <div className="ml-auto">
                        <div className="flex items-center gap-x-[--spacing-items] text-base font-medium">
                            <ActionButton
                                size="auto"
                                variant="can-hover"
                                onClick={() => {
                                    updateWeekRange("previous");
                                }}
                                className="rounded-full [border:1px_solid_var(--color-stroke)] size-7 p-0"
                                actionTitle="Previous week"
                            >
                                <ChevronLeftIcon className="stroke-text" />
                            </ActionButton>
                            <span>{currentWeekRange}</span>
                            <ActionButton
                                variant="can-hover"
                                size="auto"
                                className="rounded-full [border:1px_solid_var(--color-stroke)] size-7 p-0"
                                actionTitle="Next week"
                                onClick={() => {
                                    updateWeekRange("next");
                                }}
                            >
                                <ChevronRightIcon className="stroke-text" />
                            </ActionButton>
                        </div>
                    </div>
                </div>
                <TableFiltersDisplay
                    appliedFilters={[
                        {
                            key: "client_ids[]",
                            values: filteredClientNames || [],
                        },
                        {
                            key: "provider_ids[]",
                            values: filteredProviderNames || [],
                        },
                    ]}
                    wrapperClass="mt-[--spacing-items]"
                />
            </Card>
            <div className="mt-[--spacing-header]">
                <ScheduleWeekView />
            </div>
        </div>
    );
}
